import { useState } from 'react'
import Modal from '../modal/Modal'
import FormLoader from '../loader/FormLoader'
import { post } from '../../utils/Api'
import { useGlobalContext } from '../../utils/Context'

/**
 * Download Airway Bill, with the collection consent step.
 *
 * The waybill no longer rides along in the booking payload — it is fetched
 * from its own endpoint, because handing it over is what marks the shipment
 * collected. A booking still at "Awaiting drop-off" or "Shipment Created"
 * answers 428 with the consequences, and we only retry with
 * acknowledgeCollection once the agent has accepted them.
 *
 * `awb` is the summary the booking endpoints return:
 *   { hasDocument, released, releasedAt, releasedBy, requiresConsent, blockedReason }
 */
function DownloadAwb({
  awb,
  endpoint,
  fileName,
  onReleased,
  label = 'Download Airway Bill',
}: {
  awb: any
  endpoint: string
  fileName: string
  onReleased?: () => void
  label?: string
}) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [consent, setConsent] = useState<any>(null)

  // Nothing to download: cancelled, or a 31–49kg shipment whose AWB has not
  // been attached yet. Showing a button that can only fail is worse than
  // showing none.
  if (!awb || !awb.hasDocument || awb.blockedReason) return null

  const save = (payload: any) => {
    const link = document.createElement('a')
    link.href = payload.dataUrl || `data:application/pdf;base64,${payload.base64Data}`
    link.download = fileName || payload.fileName
    link.click()
  }

  const request = async (acknowledgeCollection: boolean) => {
    setLoading(true)
    try {
      const result: any = await post(
        endpoint,
        acknowledgeCollection ? { acknowledgeCollection: true } : {},
        null,
      )

      if (result.status === 200) {
        save(result.data)
        setConsent(null)
        if (result.data.statusChanged) {
          notify('success', 'Waybill downloaded. Shipment marked as collected.')
          // The status on the page is now stale — the booking moved to DRF.
          if (onReleased) onReleased()
        }
        return
      }

      // 428: the shipment has not been collected yet and the agent has not
      // accepted what downloading does.
      if (result.status === 428 && result.data?.requiresConsent) {
        setConsent(result.data)
        return
      }

      notify('error', result.message || 'Could not download the waybill')
    } catch {
      notify('error', 'Could not download the waybill')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button disabled={loading} onClick={() => request(false)}>
        {loading && !consent ? 'Preparing…' : label}
      </button>

      {consent && (
        <Modal position='center'>
          <div className='payment-modal'>
            <h1>Mark as collected?</h1>
            <p>
              This shipment is still{' '}
              <b>{consent.currentStatus?.description}</b>. Downloading the
              waybill marks it as <b>{consent.resultingStatus?.description}</b>.
            </p>

            <div className='awb-warning'>
              <p>
                <b>This cannot be undone.</b> There is no way to move the
                shipment back once it has been marked collected.
              </p>
              {consent.cancellationWindowCloses && (
                <p>
                  The customer will no longer be able to cancel this booking,
                  and neither will you outside of a master cancellation.
                </p>
              )}
            </div>

            <p>Only continue if you have the parcel.</p>

            {loading ? (
              <FormLoader position='center' />
            ) : (
              <div className='buttons flex justify-center'>
                <button onClick={() => setConsent(null)}>Cancel</button>
                <button className='proceed' onClick={() => request(true)}>
                  Download &amp; mark collected
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

export default DownloadAwb
